import { Skeleton } from '@mui/material';
import {
  ColumnDef,
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header,
  Row,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import cx from 'classnames';
import { ChevronDown, ChevronSelectorVertical, ChevronUp } from 'ethena-icons';
import _ from 'lodash';
import { ReactElement, ReactNode } from 'react';
import css from './SortableTable.module.scss';

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'center' | 'right';
    className?: string;
    isNoWrap?: boolean;
    minWidth?: number;
    width?: number;
  }
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

interface ITableHeaderCellProps<T> {
  header: Header<T, unknown>;
  onSort?: (sortBy: string, sortDirection: SortDirection) => void;
}

const TableHeaderCell = <T extends unknown>({
  header,
  onSort,
}: ITableHeaderCellProps<T>) => {
  const props = {
    key: header.id,
    colSpan: header.colSpan,
    align: header.column.columnDef.meta?.align,
    style: {
      // Is used for non-fixed tables
      minWidth: header.column.columnDef.meta?.minWidth,
      // Is used for fixed tables
      width: header.column.columnDef.meta?.width,
    },
  };

  const isSortable = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();

  if (!isSortable) {
    return (
      <th {...props}>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    );
  }

  const handleSort = () => {
    const newSortDirection =
      sortDirection === SortDirection.DESC
        ? SortDirection.ASC
        : SortDirection.DESC;
    header.column.toggleSorting(newSortDirection === SortDirection.DESC);
    onSort?.(header.column.id, newSortDirection);
  };

  const renderSortIcon = () => {
    if (!sortDirection) {
      return <ChevronSelectorVertical />;
    } else if (sortDirection === SortDirection.DESC) {
      return <ChevronDown />;
    } else {
      return <ChevronUp />;
    }
  };

  return (
    <th className={css.sortable} {...props} onClick={handleSort}>
      {flexRender(header.column.columnDef.header, header.getContext())}
      {renderSortIcon()}
    </th>
  );
};

interface ITableRowProps<T> {
  children?: ReactNode;
  className?: string;
  isSelected?: boolean;
  onClick?: (row: Row<T>) => void;
  row: Row<T>;
}

const TableRow = <T,>({
  children,
  className,
  isSelected,
  onClick,
  row,
}: ITableRowProps<T>) => {
  const isClickable = !!onClick;
  return (
    <tr
      className={cx(className, {
        [css.clickable]: isClickable,
        [css.selected]: isSelected,
      })}
      onClick={() => (isClickable ? onClick(row) : undefined)}
    >
      {_.map(row.getVisibleCells(), (cell) => {
        const dataColumn = cell.column.columnDef;
        return (
          <td
            align={dataColumn.meta?.align}
            className={cx(dataColumn.meta?.className, {
              [css.noWrap]: dataColumn.meta?.isNoWrap,
            })}
            key={cell.id}
          >
            {flexRender(dataColumn.cell, cell.getContext())}
          </td>
        );
      })}
      {children}
    </tr>
  );
};

interface ITableProps<T> {
  children?: (rows: Row<T>[]) => ReactElement;
  columns: ColumnDef<T>[];
  columnVisibility?: Record<string, boolean>;
  initialSort?: ColumnSort | ColumnSort[];
  isFixed?: boolean;
  isLoading?: boolean;
  onRowClick?: (row: Row<T>) => void;
  onSort?: (sortBy: string, sortDirection: SortDirection) => void;
  renderEmptyTable?: () => ReactElement;
  rowClassName?: string;
  rows: T[];
}

const SortableTable = <T extends unknown>({
  children,
  columns,
  columnVisibility,
  initialSort,
  isFixed,
  isLoading,
  onRowClick,
  onSort,
  renderEmptyTable,
  rowClassName,
  rows,
}: ITableProps<T>) => {
  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      columnVisibility,
      sorting: _.isArray(initialSort) ? initialSort : [initialSort],
    },
  });

  return (
    <>
      <table className={cx(css.table, { [css.fixed]: isFixed })}>
        <thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <tr key={headerGroup.id}>
              {_.map(headerGroup.headers, (header) => (
                <TableHeaderCell<T>
                  header={header}
                  key={header.id}
                  onSort={onSort}
                />
              ))}
            </tr>
          ))}
        </thead>

        {isLoading ? (
          <tbody>
            {_.map(_.range(3), (id) => (
              <tr key={id}>
                {_.map(table.getAllLeafColumns(), (_blank, i) => (
                  <td key={i}>
                    <Skeleton />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            {children?.(table.getRowModel().rows) ||
              _.map(table.getRowModel().rows, (row) => (
                <TableRow
                  className={rowClassName}
                  key={row.id}
                  onClick={onRowClick}
                  row={row}
                />
              ))}
          </tbody>
        )}
      </table>

      {!isLoading &&
        table.getRowModel().rows.length === 0 &&
        renderEmptyTable?.()}
    </>
  );
};

SortableTable.Row = TableRow;

export { SortableTable };
